import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { compact, countBy, isEmpty, mapValues, omitBy, isUndefined } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { PusherEventName } from 'anchor-scout-common/types';
import { TableList, Page, PageEmptyState, PageErrorState, FilterPickerGroup, PageContentHeader, Spinner, ConfirmationModal, Typography, DropdownMenu, ChipButton, Button, useStateInRef, DropdownMenuItem } from 'seagull';
import { ChevronDown, ChevronUp, Download, EmailSent, Import } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import TableFilters from '~/components/TableFilters/TableFilters';
import { ErrorText, recurrencePeriodsTextsPath } from '~/constants';
import { useOverlayContext, useIoContext, useStore, useStoreActions } from '~/contexts';
import { useDuplicateProposal } from '~/hooks/reactQuery/mutations/useDuplicateProposal';
import { useAgreements } from '~/hooks/reactQuery/queries/useAgreements';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useRedirectToProposal } from '~/hooks/useCreateAgreement';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useEnterAccountantAccess } from '~/hooks/useEnterAccountantAccess';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useOnMount } from '~/hooks/useOnMount';
import { useOpenEditAgreementModal } from '~/hooks/useOpenEditAgreementModal';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { useBulkActionModal } from '~/hooks/useTerminateAgreement/useBulkActionModal';
import { useVerifyPlaidBankAccount } from '~/hooks/useVerifyPlaidBankAccount';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise, handleRequest } from '~/infra/utils';
import { relationshipModel, relationshipSummaryModel } from '~/models';
import { PlaidBankAccountStatusTypes } from '~/models/payment-method';
import { useSubscribeToBillingObservable } from '~/providers/BillingObservableProvider';
import { BusinessChannelEventBinder, PaymentMethodEventType, PusherEvent } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import { useAppConfig, useStoreStatic } from '~/store/store-context';
import { helpArticles } from '~/utils/helpArticles';
import pluralize from '~/utils/pluralize';
import { buildRelationshipsParamsFilter } from '~/utils/relationships-params-filter-builder';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import SeeWhatItsLike from '../../components/SeeWhatItsLike/SeeWhatItsLike';
import styles from './AgreementsDashboard.module.scss';
import { sortAgreements, HeaderColId } from './AgreementsDashboard.utils';
import { serializeFilter, filtersConfig, FilterType, deserializeFiltersFromURL, defaultAgreementStatusFilter, getTableFilterLabels, getTableFilterGroups, makeTableFilterConfig, formatFilterDescription, agreementFilterTextsPath } from './agreementsDashboardFilters';
import AgreementsDashboardRow from './AgreementsDashboardRow';
import ChangeAssigneeModal from './ChangeAssigneeModal';
import { NoAgreementsIcon } from './NoAgreementsIcon';
import RenameAgreementModal from './RenameAgreementModal';
import img1 from './SeeWhatItIsLikeImages/1.png';
import img2 from './SeeWhatItIsLikeImages/2.png';
const relationshipLoader = 'getRelationship';
const columnHeadersTextsPath = 'pages.agreementsDashboard.columnHeaders';
function getTableDefs(isVendor, columnHeadersTexts, effectiveDateLabel) {
    const defs = compact([
        {
            headerItem: {
                id: HeaderColId.companyName,
                value: columnHeadersTexts.companyName()
            },
            columnDef: {
                width: 220,
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_company_name'
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: HeaderColId.status,
                value: columnHeadersTexts.status()
            },
            columnDef: {
                width: 128,
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_status'
                }
            },
            defaultSortDirection: 'asc'
        },
        isVendor && {
            headerItem: {
                id: HeaderColId.assignee,
                value: columnHeadersTexts.assignee()
            },
            columnDef: {
                width: [
                    130,
                    160
                ],
                sortable: true,
                bi: {
                    name: 'sort_assignee'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: HeaderColId.effectiveDate,
                value: effectiveDateLabel
            },
            columnDef: {
                width: [
                    112,
                    120
                ],
                sortable: true,
                bi: {
                    name: 'sort_effective_date'
                },
                mobile: {
                    label: {
                        text: effectiveDateLabel
                    }
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: HeaderColId.paymentMethod,
                value: columnHeadersTexts.paymentMethod()
            },
            columnDef: {
                width: 190,
                sortable: true,
                bi: {
                    name: 'sort_payment_method'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: HeaderColId.lastBilled,
                value: columnHeadersTexts.lastBilled()
            },
            columnDef: {
                width: 100,
                sortable: true,
                bi: {
                    name: 'sort_last_billed'
                }
            },
            defaultSortDirection: 'desc'
        }
    ]);
    const sortOptions = defs.flatMap((d, colIndex)=>d.columnDef.sortable ? [
            {
                id: d.headerItem.id,
                label: String(d.headerItem.value),
                defaultDirection: d.defaultSortDirection,
                colIndex
            }
        ] : []);
    return {
        columnDefs: defs.map((d)=>d.columnDef),
        headerItems: defs.map((d)=>d.headerItem),
        sortOptions
    };
}
export const AgreementsDashboardTestIds = pathgen('AgreementsDashboardTestIds');
export function ActualAgreementsDashboard(props) {
    const { data: _data, isRefreshing, search, filters, onChangeFilters, refresh, onChangeSearch } = props;
    const [data, setData] = useState(_data);
    useEffect(()=>setData(_data), [
        _data
    ]);
    const overlayContext = useOverlayContext();
    const { browser: { openNewTab, downloadCsv } } = useIoContext();
    const isVendor = useStore(selectors.user.isVendor);
    const { inputDebounceTime } = useAppConfig();
    const businessesWithAccountantAccess = useStore(selectors.user.businessesWithAccountantAccess);
    const { downloadAgreementPdf } = useDownloadAgreementPdf();
    const { api, logBI } = useStoreActions();
    const openEditModeModal = useOpenEditAgreementModal();
    const defaultSort = useMemo(()=>({
            colIndex: 3,
            sort: 'desc'
        }), []);
    const isMobileDevice = useIsMobileForHomePage();
    const { AGREEMENTS_CSV_EXPORT_V2 } = useFeatureFlags();
    const [exportingAllCsv, setExportingAllCsv] = useState(false);
    const [bulkExportingCsv, setBulkExportingCsv] = useState(false);
    const { redirectToProposal } = useRedirectToProposal();
    const { duplicateProposal } = useDuplicateProposal({
        onSuccess: (newDraftId)=>redirectToProposal({
                proposalType: 'agreement',
                draftId: newDraftId
            }),
        withPageLoader: true
    });
    const { terminateAgreementById } = useTerminateAgreement();
    const enterAccountantAccess = useEnterAccountantAccess();
    const verifyPlaidBankAccount = useVerifyPlaidBankAccount();
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11252, 0);
    const { doBulkAction } = useBulkActionModal();
    const { getState } = useStoreStatic();
    const businessId = useStore(selectors.user.businessId);
    const { t: agreementFilterTexts } = useLocaleKeys(agreementFilterTextsPath);
    const { t: agreementsDashboardTexts } = useLocaleKeys('pages.agreementsDashboard');
    const { t: columnHeadersTexts } = useLocaleKeys(columnHeadersTextsPath);
    const { t: recurrencePeriodsTexts } = useLocaleKeys(recurrencePeriodsTextsPath);
    const effectiveDateLabel = columnHeadersTexts.effectiveDate();
    const title = agreementsDashboardTexts.title();
    const { headerItems, columnDefs, sortOptions } = useMemo(()=>getTableDefs(isVendor, columnHeadersTexts, effectiveDateLabel), [
        isVendor
    ]);
    const sortIds = useMemo(()=>headerItems.map((item)=>item.id), [
        headerItems
    ]);
    const { sort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const [serviceNameOptions, setServiceNameOptions] = useState([]);
    const tableFilterConfig = useMemo(()=>makeTableFilterConfig({
            serviceNameOptions,
            texts: agreementFilterTexts,
            recurrencePeriodsTexts
        }), [
        serviceNameOptions
    ]);
    const filterLabels = useMemo(()=>getTableFilterLabels(agreementFilterTexts.filterLabels), []);
    const filterGroups = useMemo(()=>getTableFilterGroups(agreementFilterTexts.filterGroupLabels), []);
    const [tableSelectedFilters, setTableSelectedFilters] = useState(()=>{
        const initial = {};
        for (const type of Object.values(FilterType)){
            if (filtersConfig[type].hasValue(filters[type])) {
                initial[type] = filters[type];
            }
        }
        return initial;
    });
    const [selected, setSelected] = useState({});
    const getSelected = useStateInRef(selected);
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const agreements = useMemo(()=>{
        return sortAgreements({
            agreements: data,
            sort,
            isVendor,
            headerItems
        });
    }, [
        data,
        sort,
        isVendor,
        headerItems
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: agreements,
        scrollElRef
    });
    const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
    useOnMount(()=>{
        if (businessId) {
            api.relationship.getServiceNames(businessId, isVendor, 'agreement').then((names)=>{
                setServiceNameOptions(names.map((n)=>({
                        label: n,
                        value: n
                    })));
            });
        }
    });
    useEffect(()=>{
        // this unselects any agreement that is not found in the newly fetched list of agreements
        if (!data || isEmpty(getSelected())) return;
        const ids = data.map((agreement)=>agreement.id);
        const newSelected = mapValues(getSelected(), (origValue, agreementId)=>ids.includes(agreementId) ? origValue : false);
        setSelected(newSelected);
    }, [
        data
    ]);
    const onToggleAllSelection = useCallback((selectAll)=>{
        setSelected(agreements.reduce((acc, param)=>{
            let { id } = param;
            return Object.assign(acc, {
                [id]: selectAll
            });
        }, {}));
    }, [
        agreements
    ]);
    function onVerifyPaymentMethod(agreement) {
        if (!agreement.fundingSource) return;
        if (agreement.fundingSource.isPlaidVerification()) {
            verifyPlaidBankAccount({
                fundingSourceId: agreement.fundingSource.id(),
                relationshipId: agreement.id,
                onVerify () {
                    // Optimistic UI.
                    const newData = data.map((r)=>r.id === agreement.id ? relationshipSummaryModel.setFundingSourcePlaidVerificationStatus(r, PlaidBankAccountStatusTypes.ManuallyVerified) : r);
                    setData(newData);
                },
                onVerifyFailed () {
                    // Optimistic UI.
                    const newData = data.map((r)=>r.id === agreement.id ? relationshipSummaryModel.setFundingSourcePlaidVerificationStatus(r, PlaidBankAccountStatusTypes.VerificationFailed) : r);
                    setData(newData);
                }
            });
        }
    }
    const onSortChange = (sort)=>{
        logBI(11250, {
            column: sort ? sortIds[sort.colIndex] : 'none'
        });
        changeSort(sort);
    };
    const onClearSearch = ()=>{
        onChangeSearch('');
        debouncedLogSearchToBI.onClear();
    };
    const onDownloadPDF = (agreement)=>{
        downloadAgreementPdf(agreement.id);
    };
    const onTerminateAgreement = (agreement)=>{
        terminateAgreementById({
            id: agreement.id,
            postAction: ()=>{
                refresh();
            }
        });
    };
    const onEditAgreement = (agreement)=>{
        openEditModeModal({
            relationshipId: agreement.id,
            onUpdate: refresh
        });
    };
    const onDuplicateAgreement = (agreement)=>{
        duplicateProposal({
            relationshipId: agreement.id,
            draftId: agreement.draftId
        });
    };
    const handleTableFiltersRefresh = (newFilters, sort)=>{
        const amendments = newFilters[FilterType.HasAmendments];
        if (Array.isArray(amendments) && amendments.length > 1) {
            newFilters = _object_spread_props(_object_spread({}, newFilters), {
                [FilterType.HasAmendments]: []
            });
        }
        const changes = {};
        for (const type of Object.values(FilterType)){
            const hasNew = type in newFilters;
            if (!hasNew) {
                if (filtersConfig[type].hasValue(filters[type])) {
                    changes[type] = filtersConfig[type].getEmptyValue();
                }
            } else {
                changes[type] = newFilters[type];
            }
        }
        onChangeFilters(changes);
        changeSort(sort);
    };
    const handleSetTableSelectedFilters = (updater)=>{
        setTableSelectedFilters((prev)=>{
            const next = typeof updater === 'function' ? updater(prev) : updater;
            const amendments = next[FilterType.HasAmendments];
            if (Array.isArray(amendments) && amendments.length > 1) {
                return _object_spread_props(_object_spread({}, next), {
                    [FilterType.HasAmendments]: []
                });
            }
            return next;
        });
    };
    function getSelectedAgreements() {
        return Object.entries(selected).filter((param)=>{
            let [, isSelected] = param;
            return isSelected;
        }).map((param)=>{
            let [agreementId, _] = param;
            return agreementId;
        });
    }
    async function bulkRequestPaymentMethod(selectedAgreements) {
        logBI(11254, {
            count: selectedAgreements.length
        });
        await doBulkAction({
            bulkItemIds: selectedAgreements,
            confirmation: {
                title: agreementsDashboardTexts.requestPaymentMethod(),
                primaryButtonLabel: agreementsDashboardTexts.sendRequest(),
                content: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                'By selecting "Send Request", all clients of the selected',
                                ' ',
                                /*#__PURE__*/ _jsx("strong", {
                                    children: selectedAgreements.length
                                }),
                                " agreements will receive an email asking them to set up their payment details in Anchor."
                            ]
                        }),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: agreementsDashboardTexts.considerSendingPersonalMessage()
                        }),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(ExternalLinkButton, {
                            openInNewTab: true,
                            href: helpArticles.requestPaymentMethod(),
                            bi: {
                                name: 'agreements_request_payment_method_learn_more'
                            },
                            children: agreementsDashboardTexts.learnMoreRequestPm()
                        })
                    ]
                })
            },
            progressTitle: agreementsDashboardTexts.requestPaymentMethodProgressTitle(),
            actionName: 'sent',
            itemName: agreementsDashboardTexts.paymentMethodRequestItemName(),
            onConfirmed: ()=>{
                logBI(11255, {
                    count: selectedAgreements.length
                });
            },
            canProcessItem: (itemId)=>agreements.find((a)=>a.id === itemId && !relationshipModel.isTerminated(a)) != null,
            processSingleItem: (agreementId)=>handlePromise(api.relationship.requestPaymentMethod(agreementId)),
            onDone: (failedAgreementIds)=>{
                setSelected(failedAgreementIds.reduce((acc, agreementId)=>_object_spread_props(_object_spread({}, acc), {
                        [agreementId]: true
                    }), {}));
            }
        });
    }
    async function onRenameAgreement(agreement, onSuccess) {
        overlayContext.startLoader(relationshipLoader);
        const relationship = await handleRequest(api.relationship.get(agreement.id));
        overlayContext.stopLoader(relationshipLoader);
        if (!relationship.success) {
            overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'get-agr-error');
        } else {
            overlayContext.showModal({
                biName: 'rename_agreement_modal',
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (p)=>/*#__PURE__*/ _jsx(RenameAgreementModal, _object_spread_props(_object_spread({}, p), {
                        relationship: relationship.data,
                        onSuccess: onSuccess
                    }))
            });
        }
    }
    const openImportAgreementsModal = ()=>{
        overlayContext.showModal({
            biName: 'agreements_dashboard_agreements_dashboard_import_modal__confirm',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'agreements_confirm'
                        },
                        secondaryButton: {
                            name: 'agreements_cancel'
                        }
                    },
                    textAlignCenter: false,
                    primaryButtonLabel: agreementsDashboardTexts.importModal.close(),
                    onPrimaryButtonClick: dismissModal,
                    onCloseButtonClick: dismissModal,
                    title: agreementsDashboardTexts.importModal.title(),
                    primaryButtonTestId: "close-import-agreements-modal",
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: agreementsDashboardTexts.importModal.intro()
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                        openInNewTab: true,
                                        href: "https://forms.gle/Q9sMHXFcp5Fs866FA",
                                        bi: {
                                            name: 'import_agreements_complete_form'
                                        },
                                        children: agreementsDashboardTexts.importModal.completeForm()
                                    }),
                                    ' ',
                                    agreementsDashboardTexts.importModal.outro()
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                style: {
                                    marginTop: '16px'
                                },
                                children: agreementsDashboardTexts.importModal.note()
                            })
                        ]
                    })
                });
            }
        });
    };
    function changeAssignee(agreement) {
        overlayContext.showModal({
            biName: 'change_assignee_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (p)=>/*#__PURE__*/ _jsx(ChangeAssigneeModal, _object_spread({
                    relationship: agreement,
                    onSuccess: refresh
                }, p))
        });
    }
    const exportToCsv = ()=>{
        const mgmtHost = selectors.mgmtHost(getState());
        const filterParams = buildRelationshipsParamsFilter({
            type: 'agreement',
            isVendor,
            search,
            filters
        });
        const cleanedFilterParams = omitBy(filterParams, isUndefined);
        const searchParams = new URLSearchParams(cleanedFilterParams).toString();
        openNewTab("".concat(mgmtHost, "/business/").concat(businessId, "/relationships-summary-mv/export-csv?").concat(searchParams));
    };
    async function exportAllAgreementsCsv() {
        if (exportingAllCsv) return;
        setExportingAllCsv(true);
        try {
            const csvString = await api.relationship.exportAllAgreementsCsv({
                search,
                filters
            });
            downloadCsv(csvString, "anchor_agreements_export_".concat(format(new Date(), 'yyyy-MM-dd'), ".csv"));
            logBI(11256, {
                name: 'agreements_dashboard_export_all_csv'
            });
        } catch (unused) {
            overlayContext.showErrorToast(agreementsDashboardTexts.exportError(), 'export-agreements-csv-error');
        } finally{
            setExportingAllCsv(false);
        }
    }
    async function bulkExportAgreementsCsv() {
        if (bulkExportingCsv) return;
        setBulkExportingCsv(true);
        const ids = getSelectedAgreements();
        try {
            const csvString = await api.relationship.exportAgreementsCsvByIds(ids);
            downloadCsv(csvString, "anchor_agreements_export_".concat(format(new Date(), 'yyyy-MM-dd'), ".csv"));
            logBI(11257, {
                name: 'agreements_dashboard_bulk_export_csv'
            });
        } catch (unused) {
            overlayContext.showErrorToast(agreementsDashboardTexts.exportError(), 'export-agreements-csv-error');
        } finally{
            setBulkExportingCsv(false);
        }
    }
    const renderEmptyPage = ()=>/*#__PURE__*/ _jsx(Page, {
            header: title,
            loading: isRefreshing,
            variant: "containedHeight",
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: "agreements-empty",
                illustration: /*#__PURE__*/ _jsx(NoAgreementsIcon, {}),
                title: agreementsDashboardTexts.emptyState.title(),
                message: agreementsDashboardTexts.emptyState.message(),
                actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                            steps: [
                                {
                                    title: agreementsDashboardTexts.emptyState.step1Title(),
                                    subtitle: agreementsDashboardTexts.emptyState.step1Subtitle(),
                                    image: {
                                        src: img1,
                                        width: 1246,
                                        height: 748
                                    }
                                },
                                {
                                    title: agreementsDashboardTexts.emptyState.step2Title(),
                                    subtitle: agreementsDashboardTexts.emptyState.step2Subtitle(),
                                    image: {
                                        src: img2,
                                        width: 1246,
                                        height: 748
                                    }
                                }
                            ],
                            testId: "agreements-dashboard"
                        }),
                        /*#__PURE__*/ _jsx(CreateProposalButton, {
                            hidePrefix: true,
                            testId: "agreements-empty-new-btn"
                        })
                    ]
                })
            })
        });
    const renderAgreementsPage = ()=>/*#__PURE__*/ _jsx(Page, {
            testId: "agreements-dashboard",
            header: title,
            ref: scrollElRef,
            actions: isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(DropdownMenu, {
                        testId: AgreementsDashboardTestIds.MoreActions.Root(),
                        target: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'open_agreements_more_actions'
                            },
                            skin: "primaryOutline",
                            testId: AgreementsDashboardTestIds.MoreActions.Button(),
                            suffixIcon: /*#__PURE__*/ _jsx(ChevronDown, {}),
                            children: agreementsDashboardTexts.moreActions()
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                testId: AgreementsDashboardTestIds.MoreActions.ImportAgreementsBtn(),
                                iconStart: /*#__PURE__*/ _jsx(Download, {}),
                                onClick: ()=>openImportAgreementsModal(),
                                bi: {
                                    name: 'import_agreements'
                                },
                                children: agreementsDashboardTexts.importAgreements()
                            }),
                            AGREEMENTS_CSV_EXPORT_V2 ? /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                intent: Intent.ExportAgreementsToCsv,
                                iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                testId: AgreementsDashboardTestIds.MoreActions.ExportAgreementsBtn(),
                                onClick: exportAllAgreementsCsv,
                                disabled: exportingAllCsv,
                                bi: {
                                    name: 'agreements_dashboard_export_all_csv'
                                },
                                children: agreementsDashboardTexts.exportAll()
                            }) : /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                intent: Intent.ExportAgreementsToCsv,
                                iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                testId: AgreementsDashboardTestIds.MoreActions.ExportAgreementsBtn(),
                                onClick: exportToCsv,
                                bi: {
                                    name: 'export_agreements'
                                },
                                children: agreementsDashboardTexts.exportAgreements()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CreateProposalButton, {}, "new-agreement-button")
                ]
            }),
            onScroll: onScroll,
            children: /*#__PURE__*/ _jsxs(TableList.Provider, {
                hasActionColumn: true,
                enableRowSelection: isVendor,
                columnDefs: columnDefs,
                sort: sort,
                selectedRows: selected,
                onSortChange: onSortChange,
                onSelectionChange: setSelected,
                numRows: agreements.length,
                numSelectedRows: numSelected,
                onToggleAllSelection: onToggleAllSelection,
                children: [
                    /*#__PURE__*/ _jsxs(PageContentHeader, {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.agreementsFilters,
                                children: /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                                    flexWrap: true,
                                    testId: "agreements-dashboard-filter-bar",
                                    children: [
                                        /*#__PURE__*/ _jsx(TableFilters, {
                                            testIdPrefix: "agreements-dashboard",
                                            search: {
                                                bi: {
                                                    name: 'agreements_search'
                                                },
                                                value: search,
                                                testId: 'agreements-dashboard-search',
                                                registerHotkey: registerSearchBoxHotkey,
                                                debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
                                                placeholder: agreementsDashboardTexts.searchPlaceholder(),
                                                onChange: (event)=>{
                                                    const searchStr = event.target.value;
                                                    onChangeSearch(searchStr);
                                                    debouncedLogSearchToBI.onChange(searchStr);
                                                },
                                                onClear: onClearSearch
                                            },
                                            filterLabels: filterLabels,
                                            filterConfig: tableFilterConfig,
                                            filterGroups: filterGroups,
                                            selectedFilters: tableSelectedFilters,
                                            setSelectedFilters: handleSetTableSelectedFilters,
                                            onRefresh: handleTableFiltersRefresh,
                                            onInputFocus: ()=>setRegisterSearchBoxHotkey(false),
                                            onInputBlur: ()=>setRegisterSearchBoxHotkey(true),
                                            mobile: {
                                                formatFilterDescription: (filter, value)=>formatFilterDescription({
                                                        filterType: filter,
                                                        value,
                                                        texts: agreementFilterTexts,
                                                        recurrencePeriodsTexts
                                                    }),
                                                sortOptions
                                            }
                                        }),
                                        isVendor && !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Typography, {
                                                    className: styles.selectedItems,
                                                    testId: "agreement-dashboard-bulk-counter",
                                                    children: numSelected > 0 ? "".concat(pluralize('agreement', numSelected, true), " selected") : ''
                                                }),
                                                /*#__PURE__*/ _jsxs(DropdownMenu, {
                                                    onOpenChange: setBulkActionsOpen,
                                                    target: /*#__PURE__*/ _jsx(ChipButton, {
                                                        bi: {
                                                            name: 'agreements_bulk_actions'
                                                        },
                                                        testId: "agreement-dashboard-bulk-actions",
                                                        suffixIcon: bulkActionsOpen ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronDown, {}),
                                                        highlight: bulkActionsOpen,
                                                        disabled: numSelected === 0,
                                                        children: agreementsDashboardTexts.bulkActions()
                                                    }),
                                                    children: [
                                                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                                            iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                                                            testId: "agreement-dashboard-bulk-request-payment-method",
                                                            onClick: ()=>bulkRequestPaymentMethod(getSelectedAgreements()),
                                                            disabled: !isVendor,
                                                            bi: {
                                                                name: 'bulk_request_payment_method'
                                                            },
                                                            children: agreementsDashboardTexts.requestPaymentMethod()
                                                        }),
                                                        AGREEMENTS_CSV_EXPORT_V2 && /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                                            bi: {
                                                                name: 'agreements_dashboard_bulk_export_csv'
                                                            },
                                                            iconStart: /*#__PURE__*/ _jsx(Import, {}),
                                                            testId: "agreement-dashboard-bulk-export-csv",
                                                            disabled: bulkExportingCsv,
                                                            intent: Intent.ExportAgreementsToCsv,
                                                            onClick: ()=>bulkExportAgreementsCsv(),
                                                            children: agreementsDashboardTexts.bulkExport()
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph1",
                                weight: "light",
                                testId: AgreementsDashboardTestIds.TableHeader.AgreementsCounter(),
                                className: styles.agreementsCounter,
                                children: agreementsDashboardTexts.agreementsCount({
                                    count: data.length
                                })
                            }),
                            /*#__PURE__*/ _jsx(TableList.Header, {
                                items: headerItems,
                                testId: "agreements-dashboard-table-header"
                            })
                        ]
                    }),
                    isRefreshing ? /*#__PURE__*/ _jsx("div", {
                        className: styles.loading,
                        "data-testid": "agreements-table-loading",
                        children: /*#__PURE__*/ _jsx(Spinner, {})
                    }) : agreements.length ? paginatedData.map((agreement, index)=>/*#__PURE__*/ _jsx(AgreementsDashboardRow, {
                            agreement: agreement,
                            hasAccountantAccess: isVendor && businessesWithAccountantAccess.has(relationshipSummaryModel.customerBusinessId(agreement)),
                            isVendor: isVendor,
                            index: index,
                            onVerifyPaymentMethod: onVerifyPaymentMethod,
                            onEditAgreement: onEditAgreement,
                            onDownloadPDF: onDownloadPDF,
                            onDuplicateAgreement: onDuplicateAgreement,
                            onTerminateAgreement: onTerminateAgreement,
                            onEnterAccountantAccess: enterAccountantAccess,
                            onRenameAgreement: onRenameAgreement,
                            onChangeAssignee: changeAssignee
                        }, agreement.id)) : /*#__PURE__*/ _jsx(PageEmptyState, {
                        illustration: /*#__PURE__*/ _jsx(NoAgreementsIcon, {}),
                        title: agreementsDashboardTexts.noResults.title(),
                        message: agreementsDashboardTexts.noResults.message(),
                        testId: "agreements-empty-table"
                    })
                ]
            })
        });
    const hasActiveFilters = Object.values(FilterType).some((type)=>filtersConfig[type].hasValue(filters[type]));
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                source: "mgmt",
                eventName: PusherEvent.PaymentMethod,
                callback: (param)=>{
                    let { payload } = param;
                    if (payload.type === PaymentMethodEventType.PaymentMethodUpdated) {
                        refresh();
                    }
                }
            }),
            /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                source: "scout",
                eventName: PusherEventName.RelationshipApproved,
                callback: ()=>{
                    refresh();
                }
            }),
            data.length > 0 || hasActiveFilters || search.length || isRefreshing ? renderAgreementsPage() : renderEmptyPage()
        ]
    });
}
function AgreementsDashboardLegacy() {
    const { api } = useStoreActions();
    const { t: agreementsDashboardTexts } = useLocaleKeys('pages.agreementsDashboard');
    const title = agreementsDashboardTexts.title();
    const businessId = useStore(selectors.user.businessId);
    const isVendor = useStore(selectors.user.isVendor);
    const shouldRefresh = useRef(false);
    const refreshRef = useRef(()=>{});
    const { urlState, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        agreementStatus: {
            multi: true,
            applyDefaultOnlyOnMount: true,
            defaultValue: defaultAgreementStatusFilter
        },
        effectiveDate: {
            defaultValue: ''
        },
        lastBillingDate: {
            defaultValue: ''
        },
        paymentMethod: {
            multi: true
        },
        completion: {
            multi: true
        },
        recurrence: {
            multi: true
        },
        hasCredit: {
            multi: true
        },
        automatic: {
            multi: true
        },
        billingTrigger: {
            multi: true
        },
        serviceNames: {
            multi: true
        },
        servicePriceIncreaseState: {
            multi: true
        },
        hasAmendments: {
            multi: true
        }
    });
    const { search } = urlState;
    const deserializedFilters = useMemo(()=>deserializeFiltersFromURL(urlState), [
        urlState
    ]);
    async function refreshData() {
        if (!businessId) return Promise.resolve([]);
        return api.relationship.getSummaries({
            businessId,
            type: 'agreement',
            isVendor,
            search,
            filters: deserializedFilters
        });
    }
    useSubscribeToBillingObservable(async ()=>{
        refreshRef.current();
    });
    useSetDocumentTitle(title);
    useEffect(()=>{
        if (shouldRefresh.current) refreshRef.current();
    }, [
        urlState
    ]);
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: ()=>refreshData(),
        renderLoader: ()=>/*#__PURE__*/ _jsx(PageLoadingState, {
                header: title,
                loading: true
            }),
        renderError: (param)=>{
            let { refresh } = param;
            return /*#__PURE__*/ _jsx(PageErrorState, {
                title: title,
                onRefresh: refresh
            });
        },
        renderChild: (param)=>{
            let { data, loading, refresh } = param;
            refreshRef.current = refresh;
            function refreshOnUrlStateChange() {
                shouldRefresh.current = true;
            }
            return /*#__PURE__*/ _jsx(ActualAgreementsDashboard, {
                data: data,
                isRefreshing: loading,
                refresh: refresh,
                search: search,
                filters: deserializedFilters,
                onChangeFilters: (changes)=>{
                    refreshOnUrlStateChange();
                    const serialized = {};
                    for (const [type, value] of Object.entries(changes)){
                        serialized[type] = serializeFilter(type, value);
                    }
                    changeUrlState(serialized);
                },
                onChangeSearch: (search)=>{
                    refreshOnUrlStateChange();
                    changeUrlState({
                        search
                    });
                }
            });
        }
    });
}
function AgreementsDashboardV2() {
    const { t: agreementsDashboardTexts } = useLocaleKeys('pages.agreementsDashboard');
    const title = agreementsDashboardTexts.title();
    const { urlState, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        agreementStatus: {
            multi: true,
            applyDefaultOnlyOnMount: true,
            defaultValue: defaultAgreementStatusFilter
        },
        effectiveDate: {
            defaultValue: ''
        },
        lastBillingDate: {
            defaultValue: ''
        },
        paymentMethod: {
            multi: true
        },
        completion: {
            multi: true
        },
        recurrence: {
            multi: true
        },
        hasCredit: {
            multi: true
        },
        automatic: {
            multi: true
        },
        billingTrigger: {
            multi: true
        },
        serviceNames: {
            multi: true
        },
        servicePriceIncreaseState: {
            multi: true
        },
        hasAmendments: {
            multi: true
        }
    });
    const { search } = urlState;
    const deserializedFilters = useMemo(()=>deserializeFiltersFromURL(urlState), [
        urlState
    ]);
    const { agreements, isLoading, isFetching, refresh: refetchAgreements, error } = useAgreements({
        search,
        filters: deserializedFilters,
        refreshOnHookMount: true
    });
    async function refresh() {
        await refetchAgreements();
        return null;
    }
    useSubscribeToBillingObservable(async ()=>{
        await refetchAgreements();
    });
    useSetDocumentTitle(title);
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            title: title,
            onRefresh: refetchAgreements
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: title,
            loading: true
        });
    }
    return /*#__PURE__*/ _jsx(ActualAgreementsDashboard, {
        data: agreements,
        isRefreshing: isFetching,
        refresh: refresh,
        search: search,
        filters: deserializedFilters,
        onChangeFilters: (changes)=>{
            const serialized = {};
            for (const [type, value] of Object.entries(changes)){
                serialized[type] = serializeFilter(type, value);
            }
            changeUrlState(serialized);
        },
        onChangeSearch: (search)=>{
            changeUrlState({
                search
            });
        }
    });
}
export function AgreementsDashboard() {
    const { AGREEMENTS_CSV_EXPORT_V2 } = useFeatureFlags();
    if (AGREEMENTS_CSV_EXPORT_V2) {
        return /*#__PURE__*/ _jsx(AgreementsDashboardV2, {});
    }
    return /*#__PURE__*/ _jsx(AgreementsDashboardLegacy, {});
}
